import client from "../../auth/client";

export const getUserTableData = async (reqBody: {
  apiProps: {
    status: ["manually_added" | "pending" | "rejected" | "approved"];
    search_by?: string;
    is_active?: boolean;
  };
  queryParams: {
    page: number;
    page_size: number;
  };
}) => {
  const { page, page_size } = reqBody.queryParams;
  const { status, is_active, search_by } = reqBody.apiProps;
  const res = await client.post(
    `/auth/list_all_users/?page_size=${page_size}&page=${page}`,
    {
      status: status,
      search_by: search_by,
      is_active: is_active,
    }
  );
  return res?.data;
};

export const getAvailableRoles = async () => {
  const res = await client.get("/auth/list_user_roles/");
  return res?.data;
};

export const updateUserData = async (reqBody: {
  user_id: string;
  role_ids?: string[];
  module_access?: string[];
  is_superuser?: boolean;
  is_active?: boolean;
  request_status?: "approved" | "rejected";
}) => {
  const res = await client.put("/auth/update_user/", reqBody);
  return res?.data;
};

export const getUserStatsForUserManagement = async () => {
  const res = await client.get("/auth/get_user_stats/");
  return res.data;
};

export const addNewUser = async (reqBody: {
  email_id: string;
  role_ids?: string[];
  module_access?: string[];
  is_superuser?: boolean;
}) => {
  const res = await client.post("/auth/add_user/", reqBody);
  return res?.data;
};

export const getGlobalModules = async () => {
  const res = await client.get("/auth/modules/");
  return res?.data;
};

export const toggleGlobalModule = async (
  moduleId: string,
  reqBody: { is_enabled: boolean; reason?: string }
) => {
  const res = await client.patch(`/auth/modules/${moduleId}/`, reqBody);
  return res?.data;
};

export const listContractReviewClauseManagement = async ({
  verticalId,
  isGlobal,
  contractTypeId,
}: {
  verticalId: string;
  isGlobal?: boolean;
  contractTypeId?: string;
}) => {
  const params = new URLSearchParams();
  params.append("vertical_id", verticalId);

  if (typeof isGlobal === "boolean") {
    params.append("is_global", isGlobal ? "true" : "false");
  }

	if (contractTypeId) {
		params.append("contract_type_id", contractTypeId);
	}

  const res = await client.get(
    `/doc_review/clause_management/review_rules/redlining/list/?${params.toString()}`
  );

  const data = res.data;
  const mappedClauses = (data?.rules || []).map((rule: any) => ({
    id: rule?.rule_id,
    feature: rule?.feature,
    display_name: rule?.display_name,
    // Threshold/rule configuration is stored in rule_description for ReviewRule
    rules: rule?.rule_description,
    category: rule?.category,
    severity: rule?.severity,
    is_enabled: rule?.is_enabled,
    vertical: rule?.vertical,
    is_global: rule?.is_global,
    contract_type: rule?.contract_type,
    manual_prompt: rule?.manual_prompt ?? false,
    ai_review_prompt: rule?.ai_review_prompt ?? null,
    updated_by: rule?.updated_by,
  }));

  return {
    clauses: mappedClauses,
    page: data?.page,
    page_size: data?.page_size,
    total: data?.total,
  };
};

export const saveContractReviewClauseManagement = async (reqBody: {
  feature?: string;
  clause_id: string;
  rules: string;
  vertical_id: string;
  display_name: string;
  is_enabled: boolean;
  severity?: string;
  is_global?: boolean;
  use_override_prompt?: boolean;
  override_ai_prompt?: string;
}) => {
  const {
    clause_id,
    rules,
    vertical_id,
    display_name,
    is_enabled,
    severity,
    is_global,
    use_override_prompt,
    override_ai_prompt,
  } = reqBody;

  const payload: any = {
    rule_id: clause_id,
    vertical_id,
    display_name,
    // Persist the updated threshold/rule configuration
    rule_description: rules,
    is_enabled,
  };

  if (severity) {
    payload.severity = severity;
  }

  if (typeof is_global === "boolean") {
    payload.is_global = is_global;
  }

  // Only send override fields when toggle is explicitly ON — gate on boolean, not text content.
  if (use_override_prompt === true) {
    payload.use_override_prompt = true;
    payload.override_ai_prompt = override_ai_prompt ?? "";
  }

  const res = await client.post(
    "/doc_review/clause_management/review_rules/redlining/update/",
    payload
  );

  const data = res.data;
  const rule = data?.rule;

  if (!rule) {
    return data;
  }

  const mappedClause = {
    id: rule?.rule_id,
    feature: rule?.feature,
    display_name: rule?.display_name,
    rules: rule?.rule_description,
    category: rule?.category,
    severity: rule?.severity,
    is_enabled: rule?.is_enabled,
    vertical: rule?.vertical,
    is_global: rule?.is_global,
    contract_type: rule?.contract_type,
    manual_prompt: rule?.manual_prompt ?? false,
    ai_review_prompt: rule?.ai_review_prompt ?? null,
    updated_by: rule?.updated_by,
  };

  return { clause: mappedClause };
};

export const createContractReviewClause = async (reqBody: {
  vertical_id: string;
  display_name: string;
  category: string;
  rule_description: string;
  is_enabled?: boolean;
  is_global?: boolean;
  contract_type_id?: string;
  severity?: string;
  use_override_prompt?: boolean;
  override_ai_prompt?: string;
}) => {
  const res = await client.post(
    "/doc_review/clause_management/review_rules/redlining/create/",
    reqBody
  );

  const rule = res?.data;

  if (!rule) {
    return null;
  }

  // Map to the same shape used elsewhere for Contract Review clauses
  return {
    id: rule?.rule_id,
    feature: rule?.feature,
    display_name: rule?.display_name,
    rules: rule?.rule_description,
    category: rule?.category,
    severity: rule?.severity,
    is_enabled: rule?.is_enabled,
    vertical: rule?.vertical,
    is_global: rule?.is_global,
    contract_type: rule?.contract_type,
    manual_prompt: rule?.manual_prompt ?? false,
    updated_by: rule?.updated_by,
  };
};

export const listTalkToContractClauseManagement = async ({
  feature,
}: {
  feature: string;
}) => {
  const res = await client.get(
    "/doc_review/clause_management/list_talk_to_my_contract_clause/"
  );
  return res.data;
};

export const saveTalkToContractClauseManagement = async (reqBody: {
  rules: string;
  clause_id: string;
}) => {
  const res = await client.post(
    "/doc_review/clause_management/update_talk_to_my_contract_clause/",
    reqBody
  );
  return res.data;
};

export const generateClauseSuggestion = async (reqBody: {
  clause_name: string;
  vertical_id: string;
  definition?: string;
  clause_id?: string;
}) => {
  const res = await client.post("/clause_check/v3/generate_clause_suggestion/", reqBody);
  return res.data;
};

export const addClauseCheckDetails = async (payload: any) => {
  const res = await client.post("/clause_check/add_clause_details/", payload);
  return res.data;
};
export const addImportClauseCheckDetails = async (payload: any) => {
  const res = await client.post("/common/admin/import_clause/", payload);
  return res.data;
};
export const addImportContractReviewDetails = async (payload: any) => {
  const apiPayload: any = {
    source_vertical_id: payload?.source_vertical_id,
    destination_vertical_id: payload?.destination_vertical_id,
    // Backwards compatibility: legacy UI sends clause_ids
    rule_ids: payload?.rule_ids || payload?.clause_ids,
    source_contract_type_id: payload?.source_contract_type_id,
    destination_contract_type_id: payload?.destination_contract_type_id,
  };

  const res = await client.post(
    "/doc_review/clause_management/review_rules/redlining/import/",
    apiPayload
  );
  return res.data;
};

export const listContractTypes = async () => {
	const res = await client.get(
		"/doc_review/clause_management/review_rules/redlining/contract-types/"
	);
	return res?.data;
};
export const enableDisableClauseCheckDetails = async (payload: any) => {
  const res = await client.post("/common/admin/update_clause/", payload);
  return res.data;
};

export const syncContract = async (payload: {
  vertical_id: string;
  source_drive_link: string;
  destination_drive_link: string;
  year: string;
}) => {
  const res = await client.post("/sync_job/trigger/", payload);
  return res.data;
};

export const getCountDataForSyncJob = async (reqBody: {
  apiProps: {
    sync_job_id: string;
  };
  queryParams: {
    page: number;
    page_size: number;
  };
}) => {
  const { sync_job_id } = reqBody.apiProps;
  const res = await client.get(
    `/sync_job/list/${sync_job_id ? sync_job_id : ""}`
  );
  return res?.data;
};

export const getAnySyncJobInProgress = async () => {
  const res = await client.get("/sync_job/any_in_progress/");
  return res?.data;
};
